// Stock health per product on Inventory Overview, and the overview totals
// above the matrix. The per-product numbers come from Inventory::StockHealth;
// the totals here are display-only sums over the rows on screen.

export type StockStatus =
  | "critical"
  | "order_soon"
  | "restocking"
  | "healthy"
  | "overstock"
  | "no_sales";

export type ProductKind = "for_sale" | "gift" | "not_moving";

export interface RestockGap {
  start_iso: string;
  end_iso: string;
}

export interface StockHealthFields {
  // Producing + Shipping units bound for this market.
  incoming: number;
  // The part of incoming with no arrival date: left out of the out-of-stock
  // days, but the product still counts as having something incoming.
  incoming_undated: number;
  // The page's tab, over the last 90 days: nothing sold or gifted
  // (not_moving), 90% or more of the units gifted (gift), or the rest.
  kind: ProductKind;
  // Not moving and first in stock in this market within the last 90 days.
  is_new: boolean;
  // Average units sold or gifted per day over the last 90 days, counting only
  // days the product could sell; null = none.
  daily_sales: number | null;
  doc: number | null;
  // Days until stock runs out, counting dated incoming, at the average daily
  // sales. Without them, on the plan when the product is planned in this
  // market. Same number as the product quick view.
  stockout_days: number | null;
  // No sales, and stock outlasts the plan: stockout_days is the plan's
  // length, a lower bound ("> N").
  stockout_after_plan: boolean;
  stockout_source: "plan" | "average" | null;
  lead_time_days: number;
  // No supplier lead time on file, so the 75-day default was used.
  lead_time_default: boolean;
  // Out-of-stock days minus lead time, on the out-of-stock day the status is
  // worked out on: with something incoming, the latest one, past
  // restock_gaps.
  buffer_days: number | null;
  // Stockouts that a shipment already on the way ends, earliest first. The
  // status skips them; stockout_days is still the first one.
  restock_gaps: RestockGap[];
  // null: stock outlasts the plan with no sales to go on ("> N"), so no status.
  status: StockStatus | null;
  // Only sent to users allowed to see costs. In the market's currency; null
  // when the exchange rate from dong is missing.
  cogs?: number | null;
  value?: number | null;
}

// What the Status badge and its hover text read; the product quick view gets
// just these.
export type StockStatusFields = Pick<
  StockHealthFields,
  | "status"
  | "kind"
  | "lead_time_days"
  | "lead_time_default"
  | "buffer_days"
  | "stockout_days"
  | "stockout_after_plan"
  | "stockout_source"
  | "restock_gaps"
>;

export const STATUS_LABEL: Record<StockStatus, string> = {
  critical: "Critical",
  order_soon: "Order soon",
  restocking: "Restocking",
  healthy: "Healthy",
  overstock: "Overstock",
  no_sales: "Not enough sales data",
};

interface OverviewRow extends StockHealthFields {
  stocks: Record<string, number>;
}

export interface StockOverview {
  stock: number;
  // null when any shown product's cost could not be converted.
  value: number | null;
  incoming: number;
  // Days the shown stock covers at the shown products' combined daily sales.
  // Products with no sales are left out of both sides; null when none sold.
  doc: number | null;
  critical: number;
  orderSoon: number;
  overstock: number;
  healthy: number;
}

// Totals over the rows on screen, counting stock only in visible warehouses.
export function summarizeStock(
  rows: OverviewRow[],
  visibleWarehouseIds: string[],
): StockOverview {
  const out: StockOverview = {
    stock: 0,
    value: 0,
    incoming: 0,
    doc: null,
    critical: 0,
    orderSoon: 0,
    overstock: 0,
    healthy: 0,
  };
  let coveredStock = 0;
  let dailySales = 0;

  for (const row of rows) {
    const stock = visibleWarehouseIds.reduce(
      (sum, id) => sum + (row.stocks[id] ?? 0),
      0,
    );
    out.stock += stock;
    if (row.cogs === null) out.value = null;
    else if (out.value !== null) out.value += stock * (row.cogs ?? 0);
    out.incoming += row.incoming;
    if (row.daily_sales) {
      coveredStock += stock;
      dailySales += row.daily_sales;
    }
    if (row.status === "critical") out.critical += 1;
    if (row.status === "order_soon") out.orderSoon += 1;
    if (row.status === "overstock") out.overstock += 1;
    if (row.status === "healthy") out.healthy += 1;
  }

  if (dailySales > 0) out.doc = Math.max(Math.floor(coveredStock / dailySales), 0);
  return out;
}

// Health column sort, cycled by clicking a sortable header: the column's
// normal order, then reversed, then the server's order (total stock, highest
// first). One column sorts at a time. Normal = most urgent first for Status,
// fewest days first for DOC and Projected out of stock, highest first for
// Value.
export type SortColumn = "value" | "doc" | "stockout" | "status";
export type SortDirection = "normal" | "reversed";
export type MatrixSort = { column: SortColumn; direction: SortDirection } | null;

const SORT_COLUMNS: readonly string[] = ["value", "doc", "stockout", "status"] satisfies SortColumn[];

export const isSortColumn = (key: string): key is SortColumn => SORT_COLUMNS.includes(key);

// Clicking another column starts it in its normal order.
export function nextSort(current: MatrixSort, column: SortColumn): MatrixSort {
  if (current?.column !== column) return { column, direction: "normal" };
  return current.direction === "normal" ? { column, direction: "reversed" } : null;
}

export function sortRows<T extends StockHealthFields>(rows: T[], sort: MatrixSort): T[] {
  if (!sort) return rows;
  switch (sort.column) {
    case "status":
      return sortByStatus(rows, sort.direction);
    case "value":
      return sortByNumber(rows, sort.direction, (r) => r.value ?? null, { highestFirst: true });
    case "doc":
      return sortByNumber(rows, sort.direction, (r) => r.doc);
    case "stockout":
      return sortByNumber(rows, sort.direction, (r) => r.stockout_days, {
        isLowerBound: (r) => r.stockout_after_plan,
      });
  }
}

// Most urgent first. "Not enough sales data" is left out: it always sorts last.
const URGENCY_RANK: Record<Exclude<StockStatus, "no_sales">, number> = {
  critical: 0,
  order_soon: 1,
  restocking: 2,
  healthy: 3,
  overstock: 4,
};

const unranked = (status: StockStatus | null): status is "no_sales" | null =>
  status === null || status === "no_sales";

// Within one status, fewer buffer days = more urgent. "Not enough sales data"
// and no status stay at the bottom in both directions. Ties keep the
// incoming order.
function sortByStatus<T extends StockHealthFields>(rows: T[], direction: SortDirection): T[] {
  const dir = direction === "normal" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (unranked(a.status) || unranked(b.status)) {
      return Number(unranked(a.status)) - Number(unranked(b.status));
    }
    const byStatus = URGENCY_RANK[a.status] - URGENCY_RANK[b.status];
    if (byStatus !== 0) return dir * byStatus;
    // Buffer days are set for every ranked status.
    return dir * (a.buffer_days! - b.buffer_days!);
  });
}

// Lowest first (highest first with highestFirst). Blank cells stay at the
// bottom in both directions. A "> N" (lower bound) sorts as more than a plain
// N. Ties keep the incoming order.
function sortByNumber<T extends StockHealthFields>(
  rows: T[],
  direction: SortDirection,
  number: (row: T) => number | null,
  { highestFirst = false, isLowerBound = () => false }: {
    highestFirst?: boolean;
    isLowerBound?: (row: T) => boolean;
  } = {},
): T[] {
  const dir = (direction === "normal") !== highestFirst ? 1 : -1;
  return [...rows].sort((a, b) => {
    const na = number(a);
    const nb = number(b);
    if (na === null || nb === null) return Number(na === null) - Number(nb === null);
    if (na !== nb) return dir * (na - nb);
    return dir * (Number(isLowerBound(a)) - Number(isLowerBound(b)));
  });
}
